import type { CrawlRun } from '@/hooks/useCrawlRuns';

// Client-side shaping of the crawl-run feed. The API returns a flat list of
// runs (newest first); the ops view derives per-roaster health, day buckets,
// and cron "batches" from it — mirroring the design prototype, which computes
// the same views from its mock runs. `now` is injectable so the time-relative
// helpers stay deterministic under test.

export type CrawlRunFilter = 'all' | 'manual' | 'failed';

const CONSECUTIVE_FAILURE_ALERT_THRESHOLD = 3;
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export function relTime(iso: string, now: number = Date.now()): string {
  const diff = now - new Date(iso).getTime();
  const minutes = Math.floor(diff / MINUTE_MS);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${String(minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)}h ago`;
  return `${String(Math.floor(hours / 24))}d ago`;
}

export function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function durationSec(startIso: string, finishIso: string | null): number | null {
  if (!finishIso) return null;
  return Math.max(1, Math.round((new Date(finishIso).getTime() - new Date(startIso).getTime()) / 1000));
}

// Condenses a raw crawl error into a short, scannable label. Only a 3-digit
// code in an HTTP context (the crawler emits "fetch failed: 403 …", or a
// "status"/"HTTP" cue) is labeled as a status — a bare number elsewhere (e.g.
// "Timeout after 30000ms") is not.
export function shortError(error: string | null): string {
  if (!error) return '';
  if (error.startsWith('partial write:')) return 'Partial write';
  const code = /(?:failed:|status|http)[^\d]{0,5}(\d{3})\b/i.exec(error)?.[1];
  return code ? `HTTP ${code} fetch error` : error.slice(0, 40);
}

function startOfDayMs(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function dayLabel(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  const diffDays = Math.round((startOfDayMs(now) - startOfDayMs(then)) / DAY_MS);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export interface RoasterHealth {
  roasterId: string;
  roasterName: string;
  consecutiveFailures: number;
  alerting: boolean;
  lastRun: CrawlRun;
}

// Roasters currently alerting (>= 3 terminal failures in a row from the most
// recent run backwards), worst first. Running runs are not yet a verdict.
export function alertingRoasters(runs: CrawlRun[]): RoasterHealth[] {
  const byRoaster = new Map<string, CrawlRun[]>();
  for (const run of runs) {
    const list = byRoaster.get(run.roasterId);
    if (list) list.push(run);
    else byRoaster.set(run.roasterId, [run]);
  }

  const health: RoasterHealth[] = [];
  for (const list of byRoaster.values()) {
    const terminal = [...list]
      .filter((run) => run.status === 'ok' || run.status === 'failed')
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
    const last = terminal[0];
    if (!last) continue;
    let streak = 0;
    for (const run of terminal) {
      if (run.status !== 'failed') break;
      streak++;
    }
    health.push({
      roasterId: last.roasterId,
      roasterName: last.roasterName,
      consecutiveFailures: streak,
      alerting: streak >= CONSECUTIVE_FAILURE_ALERT_THRESHOLD,
      lastRun: last,
    });
  }

  return health
    .filter((entry) => entry.alerting)
    .sort((a, b) => b.consecutiveFailures - a.consecutiveFailures);
}

export interface CrawlBatch {
  batchId: string;
  trigger: string;
  label: string;
  roasterCount: number;
  startedAt: string;
  single: boolean;
  runs: CrawlRun[];
  firstRun: CrawlRun;
  failed: CrawlRun[];
  warned: CrawlRun[];
  okClean: CrawlRun[];
  failedCount: number;
  warnCount: number;
  okCount: number;
}

// Every run belongs to exactly one batch via the server-assigned `batchId`: a
// cron pass shares one id across all its roasters, a manual run gets its own.
// A one-member batch renders as a plain run row; a many-member batch collapses.
export function crawlBatches(runs: CrawlRun[]): CrawlBatch[] {
  const byBatch = new Map<string, CrawlRun[]>();
  for (const run of runs) {
    const list = byBatch.get(run.batchId);
    if (list) list.push(run);
    else byBatch.set(run.batchId, [run]);
  }

  const batches: CrawlBatch[] = [];
  for (const [batchId, list] of byBatch) {
    const firstRun = list[0];
    if (!firstRun) continue;
    const failed = list.filter((run) => run.status === 'failed');
    const okRuns = list.filter((run) => run.status === 'ok');
    const warned = okRuns.filter((run) => run.warnings.length > 0);
    const okClean = okRuns.filter((run) => run.warnings.length === 0);
    const startedAt = list.reduce(
      (earliest, run) => (run.startedAt < earliest ? run.startedAt : earliest),
      firstRun.startedAt,
    );
    batches.push({
      batchId,
      trigger: firstRun.trigger,
      label: 'Nightly crawl',
      roasterCount: list.length,
      startedAt,
      single: list.length === 1,
      runs: list,
      firstRun,
      failed,
      warned,
      okClean,
      failedCount: failed.length,
      warnCount: warned.length,
      // Only genuinely-finished ok runs — a mid-sweep `running` run is neither
      // ok nor failed, so it is not reported as done.
      okCount: okRuns.length,
    });
  }
  return batches;
}

export function crawlFilterCounts(runs: CrawlRun[]): Record<CrawlRunFilter, number> {
  const batches = crawlBatches(runs);
  return {
    all: batches.length,
    manual: batches.filter((batch) => batch.trigger === 'manual').length,
    failed: runs.filter((run) => run.status === 'failed').length,
  };
}

export type FeedEntry =
  | { type: 'run'; run: CrawlRun; time: string }
  | { type: 'batch'; batch: CrawlBatch; time: string };

export interface DayGroup {
  label: string;
  entries: FeedEntry[];
}

// The day-grouped feed for a filter. "manual" surfaces manual runs; "failed"
// surfaces failing runs directly (cron or not, for failure-hunting); "all"
// shows cron batches collapsed and everything else as rows.
export function buildCrawlFeed(
  runs: CrawlRun[],
  filter: CrawlRunFilter,
  now: number = Date.now(),
): DayGroup[] {
  const batches = crawlBatches(runs);

  let entries: FeedEntry[];
  if (filter === 'manual') {
    entries = batches
      .filter((batch) => batch.trigger === 'manual')
      .map((batch) => ({ type: 'run', run: batch.firstRun, time: batch.startedAt }));
  } else if (filter === 'failed') {
    entries = runs
      .filter((run) => run.status === 'failed')
      .map((run) => ({ type: 'run', run, time: run.startedAt }));
  } else {
    entries = batches.map((batch) =>
      batch.single
        ? { type: 'run', run: batch.firstRun, time: batch.startedAt }
        : { type: 'batch', batch, time: batch.startedAt },
    );
  }

  entries.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

  const groups: DayGroup[] = [];
  for (const entry of entries) {
    const label = dayLabel(entry.time, now);
    let group = groups.find((candidate) => candidate.label === label);
    if (!group) {
      group = { label, entries: [] };
      groups.push(group);
    }
    group.entries.push(entry);
  }
  return groups;
}
